/**
 * GeneratedImagesPanel
 *
 * Right panel showing generated images that can be dragged to channels.
 * Part of the ProductAssignmentSlideOver component.
 *
 * @accessibility WCAG 2.1 AA + BFSG konform
 */

import { useDraggable } from '@dnd-kit/core';
import { Sparkles, Upload, Copy, GripVertical } from 'lucide-react';
import { Badge, Button } from '@/components/v2/ui';
import { cn } from '@/lib/utils';
import type { GeneratedImage } from './ProductAssignmentSlideOver';

interface SessionInfo {
  timestamp: Date;
  promptSummary?: string;
}

interface PendingAssignment {
  assetId: string;
  channelId: string;
  channelKey: string;
}

interface GeneratedImagesPanelProps {
  images: GeneratedImage[];
  sessionInfo?: SessionInfo;
  onAddAllToGlobal?: () => void;
  pendingAssignments: PendingAssignment[];
}

export function GeneratedImagesPanel({
  images,
  sessionInfo,
  onAddAllToGlobal,
  pendingAssignments,
}: GeneratedImagesPanelProps) {
  // Count how many channels each image is assigned to
  const getAssignmentCount = (assetId: string) => {
    return pendingAssignments.filter(p => p.assetId === assetId).length;
  };

  const formatTime = (date?: Date) => {
    if (!date) return 'Heute';
    return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="flex flex-col h-full">
      {/* Panel Header */}
      <div className="px-5 py-4 border-b border-primary-500/20">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/20">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <h2 className="text-sm font-semibold text-white">Generierte Bilder</h2>
            <p className="text-xs text-neutral-400">
              Session: {formatTime(sessionInfo?.timestamp)}
              {sessionInfo?.promptSummary && (
                <>
                  <span className="text-neutral-600 mx-1.5">•</span>
                  <span className="text-primary-400">{sessionInfo.promptSummary}</span>
                </>
              )}
            </p>
          </div>
          <Badge variant="warning">{images.length} Bilder</Badge>
        </div>
      </div>

      {/* Instruction Banner */}
      <div className="mx-5 mt-4 px-4 py-3 rounded-lg bg-primary-500/10 border border-primary-500/20">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-primary-500/20 flex items-center justify-center flex-shrink-0">
            <GripVertical className="w-4 h-4 text-primary-400" />
          </div>
          <div>
            <p className="text-xs font-medium text-primary-300">Drag & Drop</p>
            <p className="text-xs text-neutral-400">Ziehe Bilder in die Kanäle links</p>
          </div>
        </div>
      </div>

      {/* Images Grid */}
      <div className="flex-1 overflow-y-auto p-5">
        <div className="grid grid-cols-2 gap-3">
          {images.map((image, index) => (
            <DraggableImageCard
              key={image.assetId}
              image={image}
              index={index}
              assignmentCount={getAssignmentCount(image.assetId)}
            />
          ))}
        </div>

        {/* Quick Actions */}
        <div className="mt-4 flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onAddAllToGlobal}
            className="flex-1 text-xs"
          >
            <Upload className="w-3.5 h-3.5 mr-2" />
            Alle zu Global
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="flex-1 text-xs"
            disabled
          >
            <Copy className="w-3.5 h-3.5 mr-2" />
            Alle kopieren
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// DraggableImageCard Sub-Component
// ============================================================================

interface DraggableImageCardProps {
  image: GeneratedImage;
  index: number;
  assignmentCount: number;
}

function DraggableImageCard({ image, index, assignmentCount }: DraggableImageCardProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `generated-${image.assetId}`,
    data: {
      asset: image,
      fromGenerated: true,
    },
  });

  const isAssigned = assignmentCount > 0;

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className={cn(
        'relative aspect-square rounded-xl border-2 overflow-hidden bg-neutral-800 group cursor-grab',
        'transition-all duration-200',
        isDragging && 'opacity-50 scale-95',
        isAssigned
          ? 'border-emerald-500/60 ring-2 ring-emerald-500/20'
          : 'border-neutral-700 hover:border-primary-500/40 hover:shadow-lg hover:shadow-primary-500/10'
      )}
      style={{ touchAction: 'none' }}
    >
      {/* Image - use s3Url if available, fallback to /image/{id} for generated images */}
      <img
        src={image.s3Url || `/image/${image.assetId}`}
        alt={`Generiertes Bild ${index + 1}`}
        className="w-full h-full object-cover"
        draggable={false}
      />

      {/* Drag Handle Indicator */}
      <div className={cn(
        'absolute top-2 left-2 w-7 h-7 rounded-lg bg-black/60 flex items-center justify-center',
        'opacity-0 group-hover:opacity-100 transition-opacity'
      )}>
        <GripVertical className="w-4 h-4 text-white" />
      </div>

      {/* Sparkle Badge */}
      <span className="absolute top-2 right-2 w-6 h-6 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-xs shadow-lg">
        <Sparkles className="w-3 h-3 text-white" />
      </span>

      {/* Assignment Count Badge */}
      {isAssigned && (
        <div className="absolute top-2 left-10 px-2 py-0.5 rounded-full bg-emerald-500 text-white text-xs font-semibold shadow-lg">
          {assignmentCount}x
        </div>
      )}

      {/* Bottom Info Bar */}
      <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between">
        <span className="px-1.5 py-0.5 text-3xs rounded bg-black/60 text-neutral-300">
          {image.width}×{image.height}
        </span>
        <span className="px-1.5 py-0.5 text-3xs rounded bg-primary-600 text-white font-medium">
          Img {index + 1}
        </span>
      </div>
    </div>
  );
}
